import { useState, useMemo, useCallback } from 'react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '~/components/ui/dialog';
import { Combobox } from '~/components/ui/combobox';
import { readFragment } from 'gql.tada';
import { useAssignInstructor } from './useRosterMutations';
import { useCreateStaffMember } from '../Staff/useStaffMemberMutations';
import { StaffMemberFragment } from '../Staff/StaffMemberFragment';

interface PersonOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface AssignInstructorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classOfferingId: string;
  schoolId: string;
  existingInstructorIds: Set<string>;
  availablePeople: PersonOption[];
  onComplete: () => void;
}

export default function AssignInstructorDialog({
  open,
  onOpenChange,
  classOfferingId,
  schoolId,
  existingInstructorIds,
  availablePeople,
  onComplete,
}: AssignInstructorDialogProps) {
  const [assignInstructor] = useAssignInstructor();
  const [createStaffMember] = useCreateStaffMember();
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [role, setRole] = useState('PRIMARY');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create new person form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const comboboxOptions = useMemo(
    () =>
      availablePeople
        .filter((p) => !existingInstructorIds.has(p.id))
        .map((p) => ({
          value: p.id,
          label: `${p.lastName}, ${p.firstName}`,
        })),
    [availablePeople, existingInstructorIds]
  );

  const resetForm = useCallback(() => {
    setSelectedPersonId('');
    setRole('PRIMARY');
    setError(null);
    setShowCreateForm(false);
    setNewFirstName('');
    setNewLastName('');
    setNewEmail('');
  }, []);

  const handleAssignExisting = useCallback(async () => {
    if (!selectedPersonId) return;
    setSaving(true);
    setError(null);
    try {
      await assignInstructor({
        variables: { input: { classOfferingId, personId: selectedPersonId, role } },
      });
      resetForm();
      onOpenChange(false);
      onComplete();
    } catch (e: any) {
      setError(e.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  }, [selectedPersonId, role, classOfferingId, assignInstructor, onOpenChange, onComplete, resetForm]);

  const handleCreateAndAssign = useCallback(async () => {
    if (!newFirstName.trim() || !newLastName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      // Create both Person + StaffMember in one mutation. Default kind=TEACHER —
      // the class-level role (PRIMARY/ASSISTANT/AIDE) is separate from staff kind.
      const result = await createStaffMember({
        variables: {
          input: {
            schoolId,
            kind: 'TEACHER',
            personAttributes: {
              firstName: newFirstName.trim(),
              lastName: newLastName.trim(),
              email: newEmail.trim() || null,
            },
          },
        },
      });

      const staffMemberMasked = result.data?.createStaffMember?.staffMember;
      const staffMember = staffMemberMasked ? readFragment(StaffMemberFragment, staffMemberMasked) : null;
      const personId = staffMember?.person.id;
      const userErrors = result.data?.createStaffMember?.userErrors;
      if (userErrors?.length) {
        setError(userErrors.map((e: any) => e.message).join(', '));
        return;
      }
      if (!personId) {
        setError('Failed to create staff member');
        return;
      }

      await assignInstructor({
        variables: { input: { classOfferingId, personId, role } },
      });
      resetForm();
      onOpenChange(false);
      onComplete();
    } catch (e: any) {
      setError(e.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  }, [newFirstName, newLastName, newEmail, role, schoolId, classOfferingId, createStaffMember, assignInstructor, onOpenChange, onComplete, resetForm]);

  const handleCreateNew = useCallback((searchValue: string) => {
    const parts = searchValue.split(',').map((s) => s.trim());
    if (parts.length >= 2) {
      setNewLastName(parts[0]);
      setNewFirstName(parts[1]);
    } else {
      setNewLastName(searchValue);
      setNewFirstName('');
    }
    setShowCreateForm(true);
  }, []);

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) resetForm(); }}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Assign Instructor</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          {!showCreateForm ? (
            <>
              <div className="space-y-2">
                <Label>Search for a person</Label>
                <Combobox
                  options={comboboxOptions}
                  value={selectedPersonId}
                  onValueChange={setSelectedPersonId}
                  placeholder="Select a person..."
                  searchPlaceholder="Type a name..."
                  emptyMessage="No matching people found."
                  allowCreate
                  onCreateNew={handleCreateNew}
                />
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRIMARY">Primary</SelectItem>
                    <SelectItem value="ASSISTANT">Assistant</SelectItem>
                    <SelectItem value="AIDE">Aide</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Create a new staff member (kind: Teacher) and assign them as instructor for this class.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>First Name *</Label>
                  <Input
                    value={newFirstName}
                    onChange={(e) => setNewFirstName(e.target.value)}
                    placeholder="Jane"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name *</Label>
                  <Input
                    value={newLastName}
                    onChange={(e) => setNewLastName(e.target.value)}
                    placeholder="Smith"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email (optional)</Label>
                <Input
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="jane.smith@school.edu"
                  type="email"
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRIMARY">Primary</SelectItem>
                    <SelectItem value="ASSISTANT">Assistant</SelectItem>
                    <SelectItem value="AIDE">Aide</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          {showCreateForm && (
            <Button variant="ghost" onClick={() => setShowCreateForm(false)} className="mr-auto">
              Back
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {showCreateForm ? (
            <Button
              onClick={handleCreateAndAssign}
              disabled={saving || !newFirstName.trim() || !newLastName.trim()}
            >
              {saving ? 'Creating...' : 'Create & Assign'}
            </Button>
          ) : (
            <Button onClick={handleAssignExisting} disabled={saving || !selectedPersonId}>
              {saving ? 'Assigning...' : 'Assign'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
